/**
 * Used to calculate schemas of workflows and/or individual workflow nodes.
 * @author Michal Drewniak (michal@blaze.tech)
 */

import { inject, Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { SetRequired } from 'type-fest';

import { ColumnId, VariableInfo, WorkflowNodeId } from '@blaze-shared/api-contracts-domain';
import { RuntimeContext } from '@blaze-shared/elements-domain';
import { loggerCreate } from '@blaze-shared/logger-util';
import { SchemaService } from '@blaze-shared/schema-data-access';
import {
  BaseSchema,
  convertApiCallSchemaToWorkflowSchema,
  convertBlazeSchemaToWorkflowSchema,
  convertDatasetColumnToColumnInfo,
  DatasetColumn,
  DatasetRow,
  DatasetSchema,
  DEFAULT_WF_SCHEMA,
  findColumn,
  isColumnId,
  removeNonSelectedColumns,
  WorkflowSchema,
} from '@blaze-shared/schema-domain';
import { deepClone, singularize } from '@blaze-shared/shared-util';
import { normalizeVarName } from '@blaze-shared/variables-util';
import {
  AggregateWorkflowNode,
  AiApiWorkflowNode,
  ApiCallWorkflowNode,
  ComputeWorkflowNode,
  DatasetWorkflowNodeBase,
  DownloadWorkflowNode,
  ExecuteWorkflowNode,
  ExpandWorkflowNode,
  ExportToXlsxWorkflowNode,
  ForEachWorkflowNode,
  getDefaultFunctionAlias,
  GetUserLocationWorkflowNode,
  getWorkflowNodeById,
  IfWorkflowNode,
  ImportFileWorkflowNode,
  isAggregateNode,
  isAiApiNode,
  isApiCallNode,
  isAssignNode,
  isBreakWorkflowNode,
  isComputeNode,
  isDatasetNode,
  isDownloadNode,
  isExecuteWorkflowNode,
  isExpandNode,
  isExportToXlsxNode,
  isForEachNode,
  isGetNode,
  isGetUserLocationNode,
  isIfNode,
  isImportFileNode,
  isJoinNode,
  isLookupNode,
  isOpenDialogNode,
  isPrintToPdfNode,
  isReadCsvDataNode,
  isReadJsonDataNode,
  isReadXlsxNode,
  isSaveFileNode,
  isSelectColumnsNode,
  isStoreVariableNode,
  isTerminateWorkflowNode,
  isToJsonNode,
  isUnionNode,
  isWhileNode,
  JoinWorkflowNode,
  LookupWorkflowNode,
  OpenDialogWorkflowNode,
  PrintToPdfWorkflowNode,
  ReadCsvDataWorkflowNode,
  ReadJsonDataWorkflowNode,
  ReadXlsxWorkflowNode,
  SaveFileWorkflowNode,
  SelectColumnsWorkflowNode,
  ToJsonWorkflowNode,
  UnionWorkflowNode,
  WhileWorkflowNode,
  Workflow,
  WorkflowNode,
} from '@blaze-shared/workflow-domain';

import { IntelliExpressionsService } from '../intelli-expressions.service';
import { IntelliRuntimeContextService } from '../intelli-runtime-context.service';
import { AggregateFunctionWithGroup } from '../models/intellisense';
import { IntellisenseErrorMessage } from '../models/intellisense-error-messages';

export interface WorkflowSchemasInfo {
  inputSchemas: BaseSchema[];
  nodeInputSchemasMap: Map<string, WorkflowSchema>;
  nodeOutputSchemasMap: Map<string, WorkflowSchema>;
  schema: WorkflowSchema;
  variables: Partial<VariableInfo>[];
}

const logSchemaCalculation = loggerCreate('schema_calculation', null, 'IntelliSchemaService', null);

@Injectable({ providedIn: 'root' })
export class WorkflowSchemaCalculatorService {
  private readonly iExpressionService = inject(IntelliExpressionsService);
  private readonly iRuntimeContextService = inject(IntelliRuntimeContextService);
  private readonly schemaService = inject(SchemaService);

  constructor() {
    this.schemaService.registerCalculateSchemaForWorkflow(
      this.calculateSchemaForWorkflow.bind(this),
    );
  }

  /**
   * Calculate schema for the given workflow
   * @param workflow - Workflow for which the schema is calculated
   * @param mainInputSchema - Main input ($$input1) schema override. If omitted, taken from workflow inputSchemas.
   * @param fallbackSchema - Fallback schema when ApiCall output schema is unknown and the walk has no columns yet
   * @returns
   */
  calculateSchemaForWorkflow(
    workflow: Workflow,
    mainInputSchema?: BaseSchema | WorkflowSchema | null,
    fallbackSchema?: BaseSchema | null,
  ): WorkflowSchemasInfo {
    // Convert any input schemas to proper workflow schemas
    const baseWfSchema = mainInputSchema
      ? convertBlazeSchemaToWorkflowSchema(mainInputSchema, workflow.id)
      : DEFAULT_WF_SCHEMA;
    const fallbackWfSchema = fallbackSchema
      ? convertBlazeSchemaToWorkflowSchema(fallbackSchema, workflow.id)
      : undefined;

    let schema: WorkflowSchema = {
      ...baseWfSchema,
      columns: mainInputSchema?.columns || [],
      expandsLinks: 'expandsLinks' in baseWfSchema ? !!baseWfSchema.expandsLinks : true,
      id: workflow.id,
      isComputed: true,
      workflowId: workflow.id,
      workflowRevision: workflow.revision,
    };
    const nodeInputSchemasMap = new Map<string, WorkflowSchema>();
    const nodeOutputSchemasMap = new Map<string, WorkflowSchema>();
    const variables: Partial<VariableInfo>[] = [];
    const parsedNodes = new Set<WorkflowNodeId>(); // Keep track of parsed nodes to break any infinite loops

    let effectiveMainInputSchema = mainInputSchema
      ? schema
      : this.schemaService.getMainInputSchema(workflow);
    if (!effectiveMainInputSchema && mainInputSchema) {
      effectiveMainInputSchema = baseWfSchema;
    }
    const wfInputSchema = convertBlazeSchemaToWorkflowSchema(effectiveMainInputSchema, workflow.id);

    if (effectiveMainInputSchema?.columns?.length && workflow.startNodeId) {
      nodeInputSchemasMap.set(workflow.startNodeId, wfInputSchema);
    }

    const node = getWorkflowNodeById(workflow?.startNodeId, workflow);
    schema = this.parseBranchSchema(
      workflow,
      schema,
      node,
      parsedNodes,
      nodeInputSchemasMap,
      nodeOutputSchemasMap,
      fallbackWfSchema,
    );

    logSchemaCalculation('info', 'getSchemaForWorkflow', (tag) =>
      console.log(`${tag} Schemas for workflow ${workflow?.id}`, {
        schema,
        nodeInputSchemasMap,
        nodeOutputSchemasMap,
      }),
    );

    // Parse schemas of data branches (for example $$input2 branche for Join node)
    for (const node of workflow.nodes) {
      if (!parsedNodes.has(node.id)) {
        this.parseBranchSchema(
          workflow,
          DEFAULT_WF_SCHEMA,
          node,
          parsedNodes,
          nodeInputSchemasMap,
          nodeOutputSchemasMap,
        );
      }
    }

    for (const node of workflow.nodes) {
      if (isAssignNode(node) && node.outputName) {
        variables.push({
          name: normalizeVarName(node.outputName),
          scope: node.scope,
          value: node.value,
        });
      } else if (isStoreVariableNode(node)) {
        variables.push({
          name: normalizeVarName(node.name),
          scope: node.scope,
          value: node.value,
        });
      } else if (!parsedNodes.has(node.id)) {
        this.parseBranchSchema(
          workflow,
          { ...DEFAULT_WF_SCHEMA },
          node,
          parsedNodes,
          nodeInputSchemasMap,
          nodeOutputSchemasMap,
        );
      }
      parsedNodes.add(node.id);
    }

    // If workflow uses an assigned variable as an output, collapse to that column.
    // Type comes from variableDefinitions so Number/chart pickers can list it.
    if (workflow.output?.startsWith('@@')) {
      schema = {
        ...schema,
        columns: [this.getColumnForWorkflowOutputVariable(workflow)],
      };
    }
    schema.isComputed = true;

    const workflowSchemasInfo: WorkflowSchemasInfo = {
      inputSchemas: workflow.inputSchemas || [],
      nodeInputSchemasMap,
      nodeOutputSchemasMap,
      schema,
      variables,
    };

    return workflowSchemasInfo;
  }

  getVariablesForWorkflow(workflow: Workflow): Partial<VariableInfo>[] {
    const variables: Partial<VariableInfo>[] = [];
    for (const node of workflow.nodes) {
      if (isAssignNode(node) && node.outputName) {
        variables.push({
          name: normalizeVarName(node.outputName),
          scope: node.scope,
          value: node.value,
        });
      } else if (isStoreVariableNode(node)) {
        variables.push({
          name: normalizeVarName(node.name),
          scope: node.scope,
          value: node.value,
        });
      }
    }
    return variables;
  }

  // TODO(damian): check whether this function can be removed in favor of using getSchemaForWorkflow()
  getSchemaForWorkflowAtStep(
    workflow: Workflow,
    breakBeforeNode: (node: WorkflowNode) => boolean,
  ): WorkflowSchema | undefined {
    if (!workflow.startNodeId) {
      console.error(new Error(`Start node not set: ${workflow.id}`));
      return;
    }

    const nodes = workflow.nodes;
    let resultNodeId: string | undefined = undefined;
    let node = nodes.find((n) => n.id === workflow.startNodeId);
    while (node) {
      if (node?.nextNodeId) {
        node = nodes.find((n) => n.id === node?.nextNodeId);
      } else {
        node = undefined;
      }
      if (node && breakBeforeNode(node)) {
        resultNodeId = node.id;
        break;
      }
    }
    const { schema, nodeInputSchemasMap } = this.calculateSchemaForWorkflow(
      workflow,
      undefined,
      undefined,
    );

    if (workflow.output?.startsWith('@@')) {
      return schema;
    }

    if (!resultNodeId) {
      logSchemaCalculation('warn', 'getSchemaForWorkflowAtStep', (tag) =>
        console.warn(`${tag} No aggregate node found - returning full schema.`),
      );
      return schema;
    }
    return nodeInputSchemasMap.get(resultNodeId);
  }

  getSchemaForAggregate(schema: WorkflowSchema, node: AggregateWorkflowNode): WorkflowSchema {
    const updatedColumns: DatasetColumn[] = [];
    const groupByColumns = node.groupByColumns;

    let aggFns: AggregateFunctionWithGroup[] = [];
    if (node.functionDefs?.length) {
      aggFns = node.functionDefs.map((fnDef) => {
        return { ...fnDef, groupBy: node.groupByColumns };
      });
    } else if (node.functions?.length) {
      aggFns = this.iExpressionService.createAggregateFunctions(node.functions, groupByColumns);
    }

    // Keep columns on which we're grouping
    node.groupByColumns.forEach((colIdOrName) => {
      const col = findColumn(schema.columns, this.schemaService.getColumnQuery(colIdOrName));
      if (col) {
        updatedColumns.push(col);
      }
    });
    // Create columns for each aggregate function
    aggFns.forEach((fn) => {
      const aggColumn = findColumn(schema.columns, this.schemaService.getColumnQuery(fn.column));
      const col = { isComputed: true } as DatasetColumn;
      const columnName = aggColumn?.name || fn.column;
      let alias: string = fn.alias ? this.schemaService.replaceColumnIdsWithNames(fn.alias) : '';
      const fnName = fn.name?.toLowerCase();
      alias = alias || getDefaultFunctionAlias(fn.name, columnName);
      switch (fnName) {
        case 'array':
          col.name = alias || columnName;
          col.dataType = 'Array';
          col.columnType = 'Array';
          if (aggColumn) {
            const aggColumnInfo = convertDatasetColumnToColumnInfo(aggColumn);
            col.dataTypeDef = {
              name: null,
              columns: [{ ...aggColumnInfo }],
            };
            schema = this.addColumnOriginToSchema(schema, aggColumn, node);
          }
          updatedColumns.push(col);
          break;
        case 'count':
          col.name = alias || fn.name;
          col.dataType = 'Number';
          updatedColumns.push(col);
          schema = this.addColumnOriginToSchema(schema, col, node);
          break;
        case 'avg':
        case 'max':
        case 'median':
        case 'min':
        case 'sum':
          col.name = alias || `${fn.name}_${columnName}`;
          col.dataType = aggColumn?.dataType;
          updatedColumns.push(col);
          schema = this.addColumnOriginToSchema(schema, col, node);
          break;
        default:
          break;
      }
    });
    const updatedSchema: WorkflowSchema = { ...schema, columns: updatedColumns };
    return updatedSchema;
  }

  getSchemaForApiCall(
    schema: WorkflowSchema,
    node: ApiCallWorkflowNode | AiApiWorkflowNode,
    fallbackSchema?: WorkflowSchema | null,
    isFirstNode = false,
  ): WorkflowSchema {
    const apiCallSchema = this.schemaService.getApiCallSchema(node as ApiCallWorkflowNode);
    // Schema for the given apicall is not known. Return current schema
    if (!apiCallSchema && schema) {
      console.warn(
        `ApiCall schema not found. Using schema passed to the apicall node. Connected elements might not show proper columns`,
      );
      return schema;
    } else if (!apiCallSchema && !schema && fallbackSchema) {
      console.warn(
        `Fallback schema for ApiCall is used. Connected elements might not show proper columns`,
      );
      schema = fallbackSchema;
      schema = this.addColumnOriginToSchema(schema, schema.columns, node);
      return schema;
    } else if (!apiCallSchema && !schema && !fallbackSchema) {
      return schema;
    }

    // ApiCall is the first node, return schema of the apicall.
    if (!schema?.columns && apiCallSchema) {
      schema = convertApiCallSchemaToWorkflowSchema(apiCallSchema, schema.workflowId, schema);
      schema = this.addColumnOriginToSchema(schema, schema.columns, node);
    } else if (!schema?.columns && !apiCallSchema) {
      return DEFAULT_WF_SCHEMA;
    }

    const parsedColumns: Set<string> = new Set(); // names of parsed column names
    const updatedColumns: DatasetColumn[] = [];
    const columns: DatasetColumn[] = deepClone(schema?.columns || []);
    const apiCallColumns: DatasetColumn[] = deepClone(apiCallSchema?.columns || []);
    const apiCallColumnNames: Set<string> = new Set(apiCallColumns.map(({ name }) => name));

    columns.forEach((column) => {
      // Both schemas have the same column name. Prepend schema2 name to the second column.
      if (apiCallColumnNames.has(column.name)) {
        updatedColumns.push({ ...column, name: `${column.name}` });
        const apiCallColumn = apiCallColumns?.find(({ name }) => name === column.name);
        if (apiCallColumn) {
          const prefix = apiCallSchema?.name || node.actionName;
          const updatedColumn = {
            ...apiCallColumn,
            name: `${prefix}_${apiCallColumn.name}`,
            sourceDatasetId: apiCallSchema?.id || '',
          };
          schema = this.addColumnOriginToSchema(schema, updatedColumn, node);
          // Remove column ID so that we use column name instead. Using column ID in this case can cause column
          // name to be improperly resolved to a non-prefixed name.
          delete updatedColumn?.id;
          updatedColumns.push(updatedColumn);
        }
        parsedColumns.add(column.name);
      } else {
        updatedColumns.push({ ...column });
        parsedColumns.add(column.name);
      }
    });

    // Parse remaining columns from target dataset.
    apiCallColumns.forEach((apiCallColumn) => {
      if (!parsedColumns.has(apiCallColumn.name)) {
        updatedColumns.push({ ...apiCallColumn });
        parsedColumns.add(apiCallColumn.name);
        schema = this.addColumnOriginToSchema(schema, apiCallColumn, node);
      }
    });

    const updatedSchema: WorkflowSchema = {
      ...schema,
      columns: updatedColumns,
      name: isFirstNode ? apiCallSchema?.name || schema.name : schema.name,
    };
    return updatedSchema;
  }

  getSchemaForCompute(
    schema: WorkflowSchema,
    node: ComputeWorkflowNode,
    data?: DatasetRow[],
  ): WorkflowSchema {
    const updatedColumns = [...schema.columns];
    const col = { isComputed: true, name: '', isProfilePicture: false } as DatasetColumn;
    if (updatedColumns.find((col) => col.name === node.name)) {
      // Column with that name exists, 'Computed_{node.name}' will be created
      col.name = `Computed_${node.name}`;
      col.dataType = this.schemaService.inferColumnDataType(col.name, data);
      updatedColumns.push(col);
      schema = this.addColumnOriginToSchema(schema, col, node);
    } else {
      // Column with that name doesn't exist. Use the provided name.
      col.name = node.name;
      col.dataType = this.schemaService.inferColumnDataType(col.name, data);
      updatedColumns.push(col);
      schema = this.addColumnOriginToSchema(schema, col, node);
    }
    const updatedSchema: WorkflowSchema = { ...schema, columns: updatedColumns };
    return updatedSchema;
  }
  getSchemaForDownload(schema: WorkflowSchema, node: DownloadWorkflowNode): WorkflowSchema {
    if (!node.createZipArchive) {
      return schema;
    }
    const col: DatasetColumn = {
      columnType: 'String',
      dataType: 'String',
      isProfilePicture: false,
      name: 'url',
    };
    schema = this.addColumnOriginToSchema(schema, col, node);
    return { ...schema, columns: [col] };
  }

  getSchemaForExecuteWorkflow(
    schema: WorkflowSchema,
    node: ExecuteWorkflowNode,
    workflow: Workflow,
  ): WorkflowSchema {
    const executedWorkflowSchema = this.schemaService.getSchema<WorkflowSchema>(
      node.workflowId,
      true,
      undefined,
      workflow.id,
    );
    if (!executedWorkflowSchema) {
      return schema;
    }

    let outputSchema = this.reattributeColumnOriginsToNode(executedWorkflowSchema, node);

    if (node.mergeWithInput) {
      outputSchema = this.mergeSchemas(schema, node, outputSchema);
    }

    return outputSchema;
  }

  getSchemaForDataset(schema: WorkflowSchema, node: DatasetWorkflowNodeBase): WorkflowSchema {
    const datasetId = node.datasetId || '';
    let subDataset = '';
    let projectedColumns: string[] = [];

    if (isGetNode(node)) {
      subDataset = node.subDataset || '';
      projectedColumns = node.projectColumns ?? [];
    }

    if (!datasetId) {
      return schema;
    }
    let updatedSchema: WorkflowSchema = {
      ...schema,
      columns: [...schema.columns],
      // ids: schema?.tableIds?.length ? [...schema.tableIds] : [],
      tableIds: schema?.tableIds?.length ? [...schema.tableIds] : [],
    };
    const datasetSchema = subDataset
      ? this.getSubDatasetSchema(datasetId, subDataset)
      : this.schemaService.getSchema<DatasetSchema>(datasetId);

    if (!datasetSchema) {
      // Unable to find schema information. Error out.
      console.error(`Unable to find schema for dataset ID: ${datasetId}`);
      throw new Error(
        `${IntellisenseErrorMessage.MissingSchemaInfo} for dataset with id: ${datasetId}`,
      );
    }
    if (!updatedSchema?.id || datasetId !== updatedSchema?.id) {
      // This is the first dataset schema in the workflow or dataset ids don't match. Make it default schema.
      updatedSchema = {
        displayColumn: datasetSchema.displayColumn,
        inputName: datasetSchema.inputName,
        columnOriginNodes: schema.columnOriginNodes ?? {},
        columns: [...datasetSchema.columns],
        expandsLinks: schema.expandsLinks,
        id: schema.id,
        name: datasetSchema.name,
        tableId: datasetSchema.id,
        tableIds: [...(updatedSchema.tableIds || []), datasetSchema.id],
        tableName: datasetSchema.name,
        workflowId: schema.workflowId,
        isComputed: true,
      };
    } else if (updatedSchema?.id) {
      // This isn't the first dataset schema. Update columns and add dataset ID to the list.
      updatedSchema.columns = [...datasetSchema.columns];
      updatedSchema.tableIds = [...(updatedSchema.tableIds || []), datasetSchema.id];
    }
    updatedSchema.tableIds = Array.from(new Set(updatedSchema.tableIds)); // The list of dataset IDs should be unique.

    if (projectedColumns?.length) {
      updatedSchema.columns = removeNonSelectedColumns(updatedSchema.columns, projectedColumns);
    }

    updatedSchema = this.addColumnOriginToSchema(updatedSchema, updatedSchema.columns, node);
    return updatedSchema;
  }

  getSubDatasetSchema(datasetId: string, subDataset: string): DatasetSchema | undefined {
    const schema = this.schemaService.getSchema<DatasetSchema>(datasetId);
    if (!schema) {
      return;
    }

    const col = schema?.columns?.find((col) => col.name === subDataset);
    if (col?.dataType === 'Array') {
      const nestedColumn = col.dataTypeDef?.columns?.[0];

      if (nestedColumn?.dataType === 'Object') {
        const nestedColumns = nestedColumn?.dataTypeDef?.columns;
        const nestedSchema: DatasetSchema = {
          columns: nestedColumns || [],
          id: schema.id,
          name: nestedColumn.name || schema.name, // Default to original schema name if not found
        };
        return nestedSchema;
      } else if (nestedColumn) {
        const nestedSchema: DatasetSchema = {
          columns: [{ ...nestedColumn, name: col.name }],
          id: schema.id,
          name: col.name || schema.name,
        };
        return nestedSchema;
      }
    }

    return schema;
  }

  getSchemaForDialog(
    schema: WorkflowSchema,
    node: Partial<SetRequired<OpenDialogWorkflowNode, 'assignments' | 'id'>>,
    nodeInput?: any,
    ctx?: RuntimeContext,
  ): WorkflowSchema {
    if (node.assignments?.length) {
      const updatedColumns: DatasetColumn[] = [];
      node.assignments.forEach(({ name, value }) => {
        // Extract the column name from the assigned value.
        const assignmentId = isColumnId(name) ? name : undefined;
        const assignmentName = this.schemaService.getColumnName(name) || name;

        const column = this.findColumnFromAssignmentValue(schema, value);
        if (column) {
          // Extracted name matches column name in the existing schema.
          // Copy over the column information but modify the name to reflect the new assigned column.
          updatedColumns.push({ ...column, id: assignmentId, name: assignmentName });
        } else if (nodeInput && ctx) {
          // Extracted column name doesn't match any columns in existing schema.
          // Create new columns and infer their type.
          const context = this.iRuntimeContextService.updateRuntimeContext(ctx, nodeInput);
          const row: DatasetRow = {};
          row[assignmentName] = this.iExpressionService.eval(value, context);
          const newColumn: DatasetColumn = {
            columnType: undefined,
            dataType: this.schemaService.inferColumnDataType(assignmentName, row),
            id: assignmentId,
            isComputed: true,
            isProfilePicture: false,
            name: assignmentName,
          };
          updatedColumns.push(newColumn);
          schema = this.addColumnOriginToSchema(schema, newColumn, node);
        } else {
          const newColumn: DatasetColumn = {
            columnType: undefined,
            dataType: this.schemaService.inferColumnDataType(assignmentName),
            id: assignmentId,
            isComputed: true,
            isProfilePicture: false,
            name: assignmentName,
          };
          updatedColumns.push(newColumn);
          schema = this.addColumnOriginToSchema(schema, newColumn, node);
        }
      });
      const updatedSchema: WorkflowSchema = { ...schema, columns: updatedColumns };
      return updatedSchema;
    }
    return schema;
  }

  getSchemaForExpand(schema: WorkflowSchema, node: ExpandWorkflowNode, data?: any): WorkflowSchema {
    const colNameOrId = node.column;
    const column = findColumn(schema?.columns, this.schemaService.getColumnQuery(colNameOrId));

    let nestedSchema: WorkflowSchema | undefined;
    if (column && (column.dataType === 'Array' || column.multipleValues)) {
      const nestedColumn = column.dataTypeDef?.columns?.[0];

      if (column.columnType === 'Link') {
        nestedSchema = {
          ...schema,
          columns: [
            {
              columnType: 'String',
              dataType: 'String',
              isProfilePicture: false,
              name: 'display',
            },
            {
              columnType: 'String',
              dataType: 'String',
              isProfilePicture: false,
              name: 'rowId',
            },
          ],
        };
      } else if (nestedColumn?.dataType === 'Object') {
        const nestedColumns = nestedColumn.dataTypeDef?.columns;
        nestedSchema = {
          columnOriginNodes: schema.columnOriginNodes ?? {},
          columns: nestedColumns || [],
          displayColumn: '',
          expandsLinks: true,
          id: schema.id,
          name: nestedColumn.name || column.name,
          tableId: '',
          tableIds: [],
          tableName: '',
          workflowId: schema.workflowId,
        };
      } else if (nestedColumn?.dataType === 'File') {
        let nestedSchema: WorkflowSchema = data
          ? this.getWorkflowSchemaFromData(schema, data)
          : {
              columnOriginNodes: schema.columnOriginNodes ?? {},
              columns: [
                {
                  columnType: 'String',
                  dataType: 'String',
                  isProfilePicture: false,
                  name: 'filename',
                },
                {
                  columnType: 'Number',
                  dataType: 'Number',
                  isProfilePicture: false,
                  name: 'height',
                },
                {
                  columnType: 'String',
                  dataType: 'String',
                  isProfilePicture: false,
                  name: 'id',
                },
                {
                  columnType: 'Object',
                  dataType: 'Object',
                  isProfilePicture: false,
                  name: 'thumbnails',
                },
                {
                  columnType: 'String',
                  dataType: 'String',
                  isProfilePicture: false,
                  name: 'type',
                },
                {
                  columnType: 'String',
                  dataType: 'String',
                  isProfilePicture: false,
                  name: 'url',
                },
                {
                  columnType: 'Number',
                  dataType: 'Number',
                  isProfilePicture: false,
                  name: 'width',
                },
              ],
              displayColumn: '',
              expandsLinks: true,
              id: schema.id,
              name: nestedColumn.name || column.name,
              tableId: '',
              tableIds: [],
              tableName: '',
              workflowId: schema.workflowId,
            };
        nestedSchema = this.addColumnOriginToSchema(nestedSchema, nestedSchema.columns, node);
        return nestedSchema;
      } else if (nestedColumn) {
        nestedSchema = {
          columnOriginNodes: schema.columnOriginNodes ?? {},
          columns: [{ ...nestedColumn, name: column.name }],
          displayColumn: '',
          expandsLinks: true,
          id: schema.id,
          name: column.name,
          tableId: '',
          tableIds: [],
          tableName: '',
          workflowId: schema.workflowId,
        };
      }
    } else if (column?.dataType === 'Object') {
      const nestedColumns = column.dataTypeDef?.columns || [];
      nestedSchema = {
        columnOriginNodes: schema.columnOriginNodes ?? {},
        columns: nestedColumns || [],
        displayColumn: '',
        expandsLinks: true,
        id: '',
        name: column.name,
        tableId: '',
        tableIds: [],
        tableName: '',
        workflowId: schema.workflowId,
      };
    } else if (column?.dataType === 'GeoPoint') {
      nestedSchema = {
        columnOriginNodes: schema.columnOriginNodes ?? {},
        columns: [
          { name: 'latitude', columnType: 'Number', dataType: 'Number', isProfilePicture: false },
          { name: 'longitude', columnType: 'Number', dataType: 'Number', isProfilePicture: false },
          { name: 'type', columnType: 'String', dataType: 'String', isProfilePicture: false },
        ],
        displayColumn: '',
        expandsLinks: true,
        id: schema.id,
        name: column.name,
        tableId: '',
        tableIds: [],
        tableName: '',
        workflowId: schema.workflowId,
      };
    }

    // Node output data specified. Infer from data.
    if (!nestedSchema && data) {
      nestedSchema = this.getWorkflowSchemaFromData(schema, data);
    } else if (!nestedSchema) {
      nestedSchema = { ...schema, columns: column ? [column] : [] };
    }

    // Merge
    if (nestedSchema && node.keepCurrentInput) {
      // Remove a column that was expanded from the final result
      const columnsExpandedRemoved = schema.columns.filter(
        ({ id, name }) => id !== node.column && name !== node.column,
      );
      nestedSchema = this.mergeSchemas(
        { ...schema, columns: columnsExpandedRemoved },
        node,
        nestedSchema,
        '.',
      );
    }
    nestedSchema = this.addColumnOriginToSchema(nestedSchema, nestedSchema.columns, node);
    return nestedSchema;
  }

  /**
   * Schema for SaveFile node.
   * If the file is processed on the server, instead of data a link to S3 resource is returned and thus the schema
   * changes to reflect that change.
   */
  getSchemaForExportToXlsxNode(
    schema: WorkflowSchema,
    node: ExportToXlsxWorkflowNode,
  ): WorkflowSchema {
    const columns: DatasetColumn[] = [
      {
        columnType: 'String',
        dataType: 'String',
        isProfilePicture: false,
        name: 'downloadUrl',
      },
    ];
    schema = this.addColumnOriginToSchema(schema, columns, node);
    return { ...schema, columns };
  }

  /**
   * Schema for ForEach node.
   *
   * Calculates two types of schemas.
   * - Schema used internally within the ForEach node - this schema is available only within the node and does not
   *   propagate outside of the node (similarly to how schema doesn't go outside of IF node branches)
   * - Parts of schema modified within the ForEach node which propagate outside of the node itself. These are caused
   *   by COMPUTE node inside the ForEach node (except when COMPUTE node is also insde IF node).
   */
  getSchemaForForEachNode(
    schema: WorkflowSchema,
    node: ForEachWorkflowNode,
    workflow: Workflow,
    parsedNodes: Set<WorkflowNodeId>,
    nodeInputSchemasMap: Map<string, WorkflowSchema>,
    nodeOutputSchemasMap: Map<string, WorkflowSchema>,
  ): WorkflowSchema {
    // Calculate schema used only inside the ForEach node.
    const firstNodeInForEach = workflow.nodes.find(
      ({ id }) => id === (node as ForEachWorkflowNode).blockStartNodeId,
    );
    if (firstNodeInForEach) {
      nodeInputSchemasMap.set(firstNodeInForEach.id, { ...schema });
      this.parseBranchSchema(
        workflow,
        deepClone(schema),
        firstNodeInForEach,
        parsedNodes,
        nodeInputSchemasMap,
        nodeOutputSchemasMap,
      );
    }

    // Calculate schema which propagates outside of the ForEach node.
    // Optimize by creating a map of nodes since we'll be accessing list of nodes quite often below.
    const nodesMap = new Map<string, WorkflowNode>();
    for (const node of workflow.nodes) {
      nodesMap.set(node.id, node);
    }
    let nextNodeId: string | null = node.blockStartNodeId;
    while (nextNodeId) {
      const nextNode = nodesMap.get(nextNodeId);
      if (isComputeNode(nextNode)) {
        schema = this.getSchemaForCompute(schema, nextNode);
      }
      nextNodeId = isForEachNode(nextNode)
        ? nextNode.blockStartNodeId
        : (nextNode?.nextNodeId ?? null);
    }
    return { ...schema };
  }

  getSchemaForGetUserLocation(
    schema: WorkflowSchema,
    node: GetUserLocationWorkflowNode,
  ): WorkflowSchema {
    const columns: DatasetColumn[] = [
      {
        name: 'latitude',
        columnType: 'Number',
        dataType: 'Number',
        isProfilePicture: false,
      },
      {
        name: 'location',
        columnType: 'GeoPoint',
        dataType: 'GeoPoint',
        isProfilePicture: false,
      },
      {
        name: 'longitude',
        columnType: 'Number',
        dataType: 'Number',
        isProfilePicture: false,
      },
    ];

    schema = this.addColumnOriginToSchema(schema, columns, node);
    return { ...schema, columns };
  }

  getSchemaForImportFile(schema: WorkflowSchema, node: ImportFileWorkflowNode): WorkflowSchema {
    const columns: DatasetColumn[] = [
      {
        name: 'fileId',
        columnType: 'String',
        dataType: 'String',
        isProfilePicture: false,
      },
    ];
    schema = this.addColumnOriginToSchema(schema, columns, node);
    return { ...schema, columns };
  }

  getSchemaForIfNode(
    schema: WorkflowSchema,
    node: IfWorkflowNode,
    workflow: Workflow,
    parsedNodes: Set<WorkflowNodeId>,
    nodeInputSchemasMap: Map<string, WorkflowSchema>,
    nodeOutputSchemasMap: Map<string, WorkflowSchema>,
  ): void {
    const falseNode = workflow.nodes.find(({ id }) => id === node.targetIdForFalse);
    const trueNode = workflow.nodes.find(({ id }) => id === node.targetIdForTrue);
    if (falseNode) {
      nodeInputSchemasMap.set(falseNode.id, { ...schema });
      this.parseBranchSchema(
        workflow,
        deepClone(schema),
        falseNode,
        parsedNodes,
        nodeInputSchemasMap,
        nodeOutputSchemasMap,
      );
    }
    if (trueNode) {
      nodeInputSchemasMap.set(trueNode.id, { ...schema });
      this.parseBranchSchema(
        workflow,
        deepClone(schema),
        trueNode,
        parsedNodes,
        nodeInputSchemasMap,
        nodeOutputSchemasMap,
      );
    }
  }

  getSchemaForJoin(
    input1Schema: WorkflowSchema,
    input2Schema: BaseSchema,
    node: JoinWorkflowNode,
  ): WorkflowSchema {
    if (!input1Schema?.columns?.length && input2Schema?.columns?.length) {
      const tableIds: string[] = Array.isArray(input1Schema.tableIds)
        ? [...input1Schema.tableIds]
        : [];
      tableIds.push(input2Schema.id);
      return {
        ...this.addColumnOriginToSchema(input1Schema, input2Schema.columns, node),
        columns: input2Schema.columns,
        tableIds,
      };
    } else if (input1Schema?.columns?.length && !input2Schema?.columns?.length) {
      return input1Schema;
    } else if (!input1Schema?.columns?.length && !input2Schema?.columns?.length) {
      return input1Schema || input2Schema;
    }
    const input1Cols = new Set(input1Schema?.columns?.map((col) => col.name));
    const expression = node.condition || '';
    const conditions = this.iExpressionService.createConditions(expression);
    const colPrefix = singularize(input2Schema.name);

    if (!conditions?.length && !node.conditionList?.length) {
      return input1Schema;
    }

    const updatedColumns: DatasetColumn[] = [];
    input1Schema?.columns?.forEach((col) => {
      updatedColumns.push({ ...col });
    });
    input2Schema?.columns?.forEach((col) => {
      if (input1Cols.has(col.name)) {
        const updatedColumn = {
          ...col,
          name: `${colPrefix}.${col.name}`,
          sourceDatasetId: input2Schema.id,
        };
        // Remove column ID so that we use column name instead. Using column ID in this case can cause column
        // name to be improperly resolved to a non-prefixed name.
        delete updatedColumn.id;
        updatedColumns.push(updatedColumn);
        input1Schema = this.addColumnOriginToSchema(input1Schema, updatedColumn, node);
      }
      // input1 doesn't have the column with that name
      else if (!input1Cols.has(col.name)) {
        updatedColumns.push({ ...col, sourceDatasetId: input2Schema.id });
      }
    });

    const tableIds = input1Schema.tableId
      ? Array.from(new Set([...(input1Schema.tableIds || [input1Schema.tableId]), input2Schema.id]))
      : [];

    const columnOriginNodes = {
      ...input1Schema.columnOriginNodes,
      ...(input2Schema as WorkflowSchema).columnOriginNodes,
    };
    const updatedSchema: WorkflowSchema = {
      columnOriginNodes: columnOriginNodes ?? {},
      columns: updatedColumns,
      displayColumn: input1Schema.displayColumn,
      expandsLinks: input1Schema.expandsLinks,
      id: input1Schema.id,
      inputName: input1Schema.inputName,
      name: input1Schema.name,
      tableId: input1Schema.tableId,
      tableIds: tableIds,
      tableName: input1Schema.tableName,
      workflowId: input1Schema.workflowId,
    };
    return updatedSchema;
  }

  getSchemaForLookup(schema: WorkflowSchema, node: LookupWorkflowNode): WorkflowSchema {
    const targetSchema: DatasetSchema | undefined = this.schemaService.getSchema(node.datasetId);
    const mergeSymbol = node?.version == 2 ? '.' : '_';
    return this.mergeSchemas(schema, node, targetSchema, mergeSymbol);
  }

  getSchemaForPrintToPdf(schema: WorkflowSchema, node: PrintToPdfWorkflowNode): WorkflowSchema {
    const columns: DatasetColumn[] = [
      {
        name: 'pdfUrl',
        dataType: 'String',
        columnType: 'String',
        isProfilePicture: false,
      },
    ];
    if (node.encodeAsBase64) {
      columns.push({
        name: 'pdfBase64',
        dataType: 'String',
        columnType: 'String',
        isProfilePicture: false,
      });
    }
    schema = this.addColumnOriginToSchema(schema, columns, node);
    return { ...schema, columns };
  }

  getSchemaForReadCsvData(schema: WorkflowSchema, node: ReadCsvDataWorkflowNode): WorkflowSchema {
    if (!node.schema?.columns?.length) {
      return schema;
    }
    schema = this.addColumnOriginToSchema(schema, node.schema.columns, node);
    return {
      ...schema,
      columns: [...node.schema.columns],
    };
  }

  getSchemaForReadJsonData(schema: WorkflowSchema, node: ReadJsonDataWorkflowNode): WorkflowSchema {
    if (!node.schema?.columns?.length) {
      return schema;
    }
    schema = this.addColumnOriginToSchema(schema, node.schema.columns, node);
    return {
      ...schema,
      columns: [...node.schema.columns],
    };
  }

  getSchemaForReadXlsx(schema: WorkflowSchema, node: ReadXlsxWorkflowNode): WorkflowSchema {
    if (!node.schema?.columns?.length) {
      return schema;
    }
    schema = this.addColumnOriginToSchema(schema, node.schema.columns, node);
    return {
      ...schema,
      columns: [...node.schema.columns],
    };
  }

  /**
   * Schema for SaveFile node.
   * If the file is processed on the server, instead of data a link to S3 resource is returned and thus the schema
   * changes to reflect that change.
   */
  getSchemaForSaveFileNode(schema: WorkflowSchema, node: SaveFileWorkflowNode): WorkflowSchema {
    if (!node.uploadToS3) {
      return schema;
    }
    const columns: DatasetColumn[] = [
      {
        columnType: 'String',
        dataType: 'String',
        name: 'downloadUrl',
        isProfilePicture: false,
      },
    ];
    schema = this.addColumnOriginToSchema(schema, columns, node);
    return { ...schema, columns };
  }

  getSchemaForSelectColumns(
    schema: WorkflowSchema,
    node: SelectColumnsWorkflowNode,
  ): WorkflowSchema {
    const updatedColumns: DatasetColumn[] = [];
    for (const col of node.columns) {
      const column = findColumn(schema.columns, { id: col.column as ColumnId, name: col.column });
      if (column) {
        const updatedColumn: DatasetColumn = {
          ...column,
          name: col.alias || column.name,
          id: col.alias ? undefined : column.id, // remove column ID when column alias is specified
        };
        updatedColumns.push(updatedColumn);
        schema = this.addColumnOriginToSchema(schema, updatedColumn, node);
      }
    }
    return { ...schema, columns: updatedColumns };
  }

  getSchemaForToJsonNode(schema: WorkflowSchema, node: ToJsonWorkflowNode): WorkflowSchema {
    if (!node.name) {
      return schema;
    }
    const updatedColumns: DatasetColumn[] = deepClone(schema.columns);
    const column: DatasetColumn = {
      columnType: 'Object',
      dataType: 'Object',
      name: node.name,
      isProfilePicture: false,
    };
    updatedColumns.push(column);
    schema = this.addColumnOriginToSchema(schema, column, node);
    return {
      ...schema,
      columns: updatedColumns,
    };
  }

  getSchemaForUnionNode(schema: WorkflowSchema, node: UnionWorkflowNode): WorkflowSchema {
    let updatedColumns: DatasetColumn[] = deepClone(schema.columns);

    if (node.projectColumns.length) {
      updatedColumns = updatedColumns.filter((column) => node.projectColumns.includes(column.name));
    }

    return { ...schema, columns: updatedColumns };
  }

  getWorkflowSchemaFromData(
    schema: WorkflowSchema,
    data: any,
    name?: string,
    id?: string,
    targetSchema?: WorkflowSchema,
  ): WorkflowSchema {
    const targetColumns: DatasetColumn[] = deepClone(targetSchema?.columns || []);
    const targetColumnNames: Set<string> = new Set(targetColumns.map(({ name }) => name));
    const updatedColumns: DatasetColumn[] = [];
    const parsedColumns: Set<string> = new Set(); // names of parsed column names
    if (!data) {
      return schema;
    }

    const dataToParse: any[] = Array.isArray(data) ? data : [data];
    dataToParse.forEach((row) => {
      Object.entries(row).forEach(([colName, value]) => {
        const prefix = `${targetSchema?.name || ''}_`;
        const colNameWithoutPrefix = colName.replaceAll(prefix, '');
        const targetCol = targetColumns.find((col) => col.name === colNameWithoutPrefix);

        // Check if schema contains column with the name generated during apicall data merge. We're guaranteed that
        // it exists if the column with the same name or with the with the same name with ${name}_ prefix exists.
        // We can rely on ${name}_ because apicall always uses mergeObjects in a way that objA is the original input
        // object and objB is the response from the apicall.
        const existingCol = schema?.columns?.find((col) => col.name === colName);
        if (!parsedColumns.has(colName) && existingCol) {
          parsedColumns.add(colName);
          const newCol: DatasetColumn = { ...existingCol, name: colName };
          updatedColumns.push(newCol);
        } else if (
          !parsedColumns.has(colName) &&
          targetColumnNames.has(colNameWithoutPrefix) &&
          targetCol
        ) {
          parsedColumns.add(colName);
          updatedColumns.push({ ...targetCol, name: colName });
        } else if (!parsedColumns.has(colName)) {
          parsedColumns.add(colName);
          const newCol: DatasetColumn = {
            name: colName,
            isComputed: true,
            columnType: undefined,
            dataType: this.schemaService.inferColumnDataType(colName, value),
            isProfilePicture: false,
          };
          updatedColumns.push(newCol);
        }
      });
    });

    const tableName = schema?.tableName || name?.replaceAll(' ', '') || '';
    const updatedSchema: WorkflowSchema = {
      ...schema,
      columns: updatedColumns,
      id: schema.id || id || '',
      name: schema.name || schema.tableName || '',
      tableName: tableName,
    };
    return updatedSchema;
  }

  obtainSchema(datasetId: string): Observable<DatasetSchema | undefined> {
    return this.schemaService.obtainSchema<DatasetSchema>(datasetId);
  }

  private reattributeColumnOriginsToNode(
    schema: WorkflowSchema,
    node: Partial<SetRequired<WorkflowNode, 'id'>>,
  ): WorkflowSchema {
    return this.addColumnOriginToSchema({ ...schema, columnOriginNodes: {} }, schema.columns, node);
  }

  private addColumnOriginToSchema(
    schema: WorkflowSchema,
    column: DatasetColumn | DatasetColumn[],
    node: Partial<SetRequired<WorkflowNode, 'id'>>,
  ): WorkflowSchema {
    if (!node.id) {
      return schema;
    }
    const columns = Array.isArray(column) ? column : [column];
    const columnOriginNodes = { ...(schema.columnOriginNodes ?? {}) };
    for (const col of columns) {
      if (col) {
        const idOrName = col.id ?? col.name;
        const currOriginNodes = columnOriginNodes[idOrName] ?? [];
        if (!currOriginNodes.some((nodeId) => nodeId === node.id)) {
          columnOriginNodes[idOrName] = [...currOriginNodes, node.id];
        }
      }
    }
    return { ...schema, columnOriginNodes };
  }

  private mergeSchemas(
    schemaA: WorkflowSchema,
    node: WorkflowNode,
    schemaB?: DatasetSchema,
    mergeSymbol = '.',
  ): WorkflowSchema {
    const schemaATableIds = schemaA.tableIds || [];
    const tableIds = schemaB?.id ? schemaATableIds.concat([schemaB?.id]) : schemaATableIds;
    if (!schemaB?.columns?.length) {
      return { ...schemaA, tableIds };
    }
    if (!schemaA?.columns) {
      let updatedSchemaA = { ...schemaA };
      for (const col of schemaB.columns) {
        updatedSchemaA = this.addColumnOriginToSchema(updatedSchemaA, col, node);
      }
      return { ...updatedSchemaA, columns: schemaB.columns, tableIds };
    }

    // Gather column information from schema and target schema.
    const parsedColumns: Set<string> = new Set(); // names of parsed column names
    const updatedColumns: DatasetColumn[] = [];
    const columns: DatasetColumn[] = deepClone(schemaA?.columns);
    const targetColumns: DatasetColumn[] = deepClone(schemaB?.columns || []);
    const targetColumnNames: Set<string> = new Set(targetColumns.map(({ name }) => name));

    columns.forEach((column) => {
      // Both schemas have the same column name. Prepend schema2 name to the second column.
      if (targetColumnNames.has(column.name)) {
        updatedColumns.push({ ...column, name: `${column.name}` });
        const targetColumn = targetColumns?.find(({ name }) => name === column.name);
        if (targetColumn) {
          const colPrefix = singularize(schemaB.name);
          const updatedColumn = {
            ...targetColumn,
            name: `${colPrefix}${mergeSymbol}${targetColumn.name}`,
            sourceDatasetId: schemaB.id,
          };
          // Remove column ID so that we use column name instead. Using column ID in this case can cause column
          // name to be improperly resolved to a non-prefixed name.
          delete updatedColumn?.id;
          updatedColumns.push(updatedColumn);
          schemaA = this.addColumnOriginToSchema(schemaA, updatedColumn, node);
        }
        parsedColumns.add(column.name);
      } else {
        updatedColumns.push({ ...column });
        parsedColumns.add(column.name);
      }
    });

    // Parse remaining columns from target dataset.
    targetColumns.forEach((targetColumn) => {
      if (!parsedColumns.has(targetColumn.name)) {
        updatedColumns.push({ ...targetColumn });
        parsedColumns.add(targetColumn.name);
        schemaA = this.addColumnOriginToSchema(schemaA, targetColumn, node);
      }
    });

    const updatedSchema: WorkflowSchema = { ...schemaA, columns: updatedColumns };
    return updatedSchema;
  }

  /**
   * Schema column for a workflow that returns an assigned variable (`@@…`).
   * Type comes from variableDefinitions; falls back to String when unknown.
   */
  private getColumnForWorkflowOutputVariable(workflow: Workflow): DatasetColumn {
    const outputName = workflow.output as `@@${string}`;
    const dataType =
      workflow.variableDefinitions?.find(
        (def) => normalizeVarName(def.name) === normalizeVarName(outputName),
      )?.dataType ?? 'String';

    return {
      columnType: dataType,
      dataType,
      isProfilePicture: false,
      name: outputName,
    };
  }

  private findColumnFromAssignmentValue(
    schema: WorkflowSchema | undefined,
    value: unknown,
  ): DatasetColumn | undefined {
    if (typeof value !== 'string' || !value) {
      return undefined;
    }
    const assignmentValueIdOrName = value
      .replace('$$input1.', '')
      .replace(/\$\$input1\['(.*)']/, '$1')
      .replace(/\$\$input1\["(.*)"]/, '$1');
    return findColumn(schema?.columns, assignmentValueIdOrName);
  }

  private parseBranchSchema(
    workflow: Workflow,
    schema: WorkflowSchema,
    node: WorkflowNode | null,
    parsedNodes: Set<WorkflowNodeId>,
    nodeInputSchemasMap: Map<string, WorkflowSchema>,
    nodeOutputSchemasMap: Map<string, WorkflowSchema>,
    fallbackWfSchema?: WorkflowSchema,
  ): WorkflowSchema {
    let node2;
    while (node && !parsedNodes.has(node.id)) {
      parsedNodes.add(node.id);
      nodeOutputSchemasMap.set(node.id, schema);
      if (node.nodeType === 'Join') {
        node2 = getWorkflowNodeById((node as JoinWorkflowNode)?.targetId, workflow);
      }

      if (isAggregateNode(node)) {
        schema = this.getSchemaForAggregate(schema, node);
      } else if (isApiCallNode(node) || isAiApiNode(node)) {
        const isFirstNode = workflow.startNodeId === node.id;
        schema = this.getSchemaForApiCall(schema, node, fallbackWfSchema, isFirstNode);
      } else if (isBreakWorkflowNode(node)) {
        nodeOutputSchemasMap.set(node.id, { ...schema });
        return schema;
      } else if (isComputeNode(node)) {
        schema = this.getSchemaForCompute(schema, node);
      } else if (isDownloadNode(node)) {
        schema = this.getSchemaForDownload(schema, node);
      } else if (isGetNode(node)) {
        schema = this.getSchemaForDataset(schema, node);
      } else if (isExecuteWorkflowNode(node)) {
        schema = this.getSchemaForExecuteWorkflow(schema, node, workflow);
      } else if (isExpandNode(node)) {
        schema = this.getSchemaForExpand(schema, node);
      } else if (isExportToXlsxNode(node)) {
        schema = this.getSchemaForExportToXlsxNode(schema, node);
      } else if (isForEachNode(node)) {
        schema = this.getSchemaForForEachNode(
          schema,
          node,
          workflow,
          parsedNodes,
          nodeInputSchemasMap,
          nodeOutputSchemasMap,
        );
      } else if (isGetUserLocationNode(node)) {
        schema = this.getSchemaForGetUserLocation(schema, node);
      } else if (isImportFileNode(node)) {
        schema = this.getSchemaForImportFile(schema, node);
      } else if (isIfNode(node)) {
        this.getSchemaForIfNode(
          schema,
          node,
          workflow,
          parsedNodes,
          nodeInputSchemasMap,
          nodeOutputSchemasMap,
        );
      } else if (isJoinNode(node)) {
        if (isGetNode(node2) || isApiCallNode(node2)) {
          const schema2 = this.parseBranchSchema(
            workflow,
            { ...DEFAULT_WF_SCHEMA },
            node2,
            parsedNodes,
            nodeInputSchemasMap,
            nodeOutputSchemasMap,
          );
          schema = this.getSchemaForJoin(schema, schema2, node);
        } else {
          logSchemaCalculation('warn', 'parseBranchSchema', (tag) =>
            console.warn(
              `${tag} - Unsupported scenario for Join node. Connected elements might not show proper columns`,
            ),
          );
          break;
        }
      } else if (isLookupNode(node)) {
        schema = this.getSchemaForLookup(schema, node);
      } else if (isOpenDialogNode(node)) {
        schema = this.getSchemaForDialog(schema, node);
      } else if (isPrintToPdfNode(node)) {
        schema = this.getSchemaForPrintToPdf(schema, node);
      } else if (isReadCsvDataNode(node)) {
        schema = this.getSchemaForReadCsvData(schema, node);
      } else if (isReadJsonDataNode(node)) {
        schema = this.getSchemaForReadJsonData(schema, node);
      } else if (isReadXlsxNode(node)) {
        schema = this.getSchemaForReadXlsx(schema, node);
      } else if (isTerminateWorkflowNode(node)) {
        nodeOutputSchemasMap.set(node.id, { ...schema });
        return schema;
      } else if (isSaveFileNode(node)) {
        schema = this.getSchemaForSaveFileNode(schema, node);
      } else if (isSelectColumnsNode(node)) {
        schema = this.getSchemaForSelectColumns(schema, node);
      } else if (isToJsonNode(node)) {
        schema = this.getSchemaForToJsonNode(schema, node);
      } else if (isUnionNode(node)) {
        schema = this.getSchemaForUnionNode(schema, node);
      } else if (isWhileNode(node)) {
        const startNode = node.blockStartNodeId
          ? workflow.nodes.find(({ id }) => id === (node as WhileWorkflowNode)?.blockStartNodeId)
          : undefined;
        if (startNode) {
          nodeInputSchemasMap.set(startNode.id, { ...schema });
          this.parseBranchSchema(
            workflow,
            deepClone(schema),
            startNode,
            parsedNodes,
            nodeInputSchemasMap,
            nodeOutputSchemasMap,
          );
        }
      } else if (isDatasetNode(node) && !isGetNode(node) && !isLookupNode(node)) {
        schema = this.getSchemaForDataset(schema, node);
      }
      nodeOutputSchemasMap.set(node.id, { ...schema });
      const nextNodeId = node?.nextNodeId;
      if (nextNodeId) {
        nodeInputSchemasMap.set(nextNodeId, { ...schema });
      }
      if (node.id === nextNodeId) {
        logSchemaCalculation('error', 'parseBranchSchema', (tag) =>
          console.error(`${tag} - Node (${node?.id}) points to itself - infinite loop error`),
        );
        break;
      }
      node = getWorkflowNodeById(nextNodeId, workflow);
    }
    return schema;
  }
}
